import { StatusType } from "@datadog/browser-logs";
import { log } from "logging/datadogBrowserLogs";
import { useEffect, useRef, useState } from "react";
import {
  addJitter,
  makePortMessageListener,
} from "websocket/useWebsocket/makePortMessageHandler";
import {
  BackendIdentifier,
  MessageHandlersForBackend,
  PortMessageEventData,
  WorkerPortId,
} from "websocket/websocketSharedWorkerTypes";

type HandlerMapSupertype = { [key: string]: Function | undefined };

function diffPortMessageHandlers(
  oldHandlers: HandlerMapSupertype,
  newHandlers: HandlerMapSupertype,
) {
  const oldKeys = Object.keys(oldHandlers);
  const newKeys = Object.keys(newHandlers);

  if (
    oldKeys.length !== newKeys.length ||
    newKeys.some((key) => !oldKeys.includes(key))
  ) {
    log(
      StatusType.error,
      "useWebsocket/diffMessageHandlers",
      `Attempted to change subscribed websocket topics after mount. It is likely better to split up your component if you are in a situation where you need variable subscriptions. Old topics: ${oldKeys.join(", ")}. New topics: ${newKeys.join(", ")}.`,
    );

    // Make it really obvious you shouldn't be doing this :)
    throw new Error(
      `Attempted to change subscribed websocket topics after mount. It is likely better to split up your component if you are in a situation where you need variable subscriptions. Old topics: ${oldKeys.join(", ")}. New topics: ${newKeys.join(", ")}.`,
    );
  }

  return newKeys.some((key) => oldHandlers[key] !== newHandlers[key]);
}

export function useWebsocket<Backend extends BackendIdentifier>(
  backendIdentifier: Backend,
  messageHandlers: Partial<MessageHandlersForBackend[Backend]>,
) {
  const worker = useRef<SharedWorker | null>(null);
  const [portId, setPortId] = useState<WorkerPortId | null>(null);

  const [storedHandlers, setStoredHandlers] = useState(messageHandlers);

  // Set up the SharedWorker connection
  useEffect(() => {
    if (!worker.current) {
      // Initialise connection to shared worker if it doesn't already exist
      worker.current = new SharedWorker(
        new URL("./WebsocketSharedWorker.ts", import.meta.url),
        {
          type: "module",
          name: backendIdentifier,
        },
      );
    }

    // Destructor: tell SharedWorker to unregister, then close port
    return () => {
      if (worker.current && portId) {
        const messageData: PortMessageEventData = {
          type: "close",
          id: portId,
        };
        worker.current.port.postMessage(messageData);
        worker.current.port.close();
      }
    };
  }, [worker.current, portId]);

  useEffect(() => {
    const changed = diffPortMessageHandlers(storedHandlers, messageHandlers);

    if (changed) {
      setStoredHandlers(messageHandlers);
    }
  }, [messageHandlers]);

  useEffect(() => {
    // Update the port listener whenever the message handlers change
    // If handler functions aren't stable this will cause an infinite rerender loop.
    // You should define your handlers outside of your component if possible,
    // or else wrap them in a useCallback so they don't change on every render.
    if (worker.current && storedHandlers) {
      // If listener is missing or handler has changed, register a new one
      const abortController = new AbortController();

      worker.current.port.addEventListener(
        "message",
        makePortMessageListener(worker.current, setPortId, storedHandlers),
        { signal: abortController.signal },
      );

      worker.current.port.start();

      return () => {
        abortController.abort();
      };
    }

    return () => {};
  }, [storedHandlers, worker.current]);

  useEffect(() => {
    if (worker.current && portId) {
      // Triggers for when to notify the SharedWorker in case the socket has become uninitialized or token has been refreshed.
      // See: https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API#concepts_and_usage
      //
      // - online:           whenever the browser regains an internet connection after being offline
      // - focus:            whenever the tab regains focus (e.g. the first time the user clicks it
      //                     after interacting with another window)
      // - visibilitychange: whenever the document visibility changes (e.g. tab switch, window
      //                     minimised/reopened).
      const controller = new AbortController();

      const sendReactivationMessage = () => {
        const message: PortMessageEventData = {
          type: "reactivated",
          id: portId,
        };

        window.setTimeout(
          () => {
            worker.current?.port.postMessage(message);
          },
          50 + addJitter(50), // 25–75 ms
        );
      };

      window.addEventListener("online", sendReactivationMessage, {
        signal: controller.signal,
      });

      window.addEventListener("focus", sendReactivationMessage, {
        signal: controller.signal,
      });

      document.addEventListener(
        "visibilitychange",
        () => {
          if (!document.hidden) {
            sendReactivationMessage();
          }
        },
        { signal: controller.signal },
      );

      // Destructor: unregister the handlers
      return () => {
        controller.abort();
      };
    }
    return () => {};
  }, [worker.current, portId]);

  return {
    connected: Boolean(worker.current && portId),
  };
}
